import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Tab,
  Tabs,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode2 as QrCodeIcon,
  Refresh as RefreshIcon,
  ArrowBack,
} from '@mui/icons-material';
import { FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

interface GiftCardDefinition {
  id: string;
  cafeId: string;
  cafeName: string;
  cafeSlug: string;
  beanCost: number;
  value: number;
  availableCodes: number;
  isActive: boolean;
  createdAt: Date;
}

interface GiftCardCode {
  id: string;
  definitionId: string;
  code: string;
  randomPart: string;
  status: 'available' | 'purchased' | 'used';
  purchasedBy?: string;
  purchasedAt?: Date;
  usedAt?: Date;
  verifiedBy?: string;
}

interface Stats {
  totalDefinitions: number;
  activeDefinitions: number;
  totalCodes: number;
  availableCodes: number;
  purchasedCodes: number;
  usedCodes: number;
  totalRevenue: number;
}

interface PartnerCafe {
  id: string;
  name: string;
  slug: string;
}

const GiftCardsManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [definitions, setDefinitions] = useState<GiftCardDefinition[]>([]);
  const [codes, setCodes] = useState<GiftCardCode[]>([]);
  const [partnerCafes, setPartnerCafes] = useState<PartnerCafe[]>([]);
  const [stats, setStats] = useState<Stats>({
    totalDefinitions: 0,
    activeDefinitions: 0,
    totalCodes: 0,
    availableCodes: 0,
    purchasedCodes: 0,
    usedCodes: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Dialog states
  const [openDefinitionDialog, setOpenDefinitionDialog] = useState(false);
  const [openCodeGenDialog, setOpenCodeGenDialog] = useState(false);
  const [editingDefinition, setEditingDefinition] = useState<GiftCardDefinition | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    cafeId: '',
    cafeName: '',
    cafeSlug: '',
    beanCost: 400,
    value: 100,
  });
  const [codeGenData, setCodeGenData] = useState({
    definitionId: '',
    count: 10,
  });

  useEffect(() => {
    loadData();
    loadPartnerCafes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    try {
      const [defsResponse, codesResponse, statsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/gift-cards/definitions`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/gift-cards/codes`, getAuthHeaders()),
        axios.get(`${API_BASE_URL}/api/gift-cards/stats`, getAuthHeaders()),
      ]);

      if (defsResponse.data.success) {
        setDefinitions(defsResponse.data.data);
      }
      if (codesResponse.data.success) {
        setCodes(codesResponse.data.data);
      }
      if (statsResponse.data.success) {
        setStats(statsResponse.data.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadPartnerCafes = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/partner-cafes`, getAuthHeaders());
      if (response.data.success) {
        const cafes = response.data.data.map((cafe: any) => ({
          id: cafe.id,
          name: cafe.name,
          slug: cafe.slug
        }));
        setPartnerCafes(cafes);
      }
    } catch (err) {
      console.error('Error loading partner cafes:', err);
    }
  };

  const handleCafeSelect = (cafeId: string) => {
    const selectedCafe = partnerCafes.find(c => c.id === cafeId);
    if (selectedCafe) {
      setFormData({
        ...formData,
        cafeId: selectedCafe.id,
        cafeName: selectedCafe.name,
        cafeSlug: selectedCafe.slug
      });
    }
  };

  const handleCreateDefinition = async () => {
    try {
      setError('');
      console.log('Sending gift card definition:', formData);
      const response = await axios.post(
        `${API_BASE_URL}/api/gift-cards/definitions`,
        formData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Gift card tanımı oluşturuldu!');
        setOpenDefinitionDialog(false);
        resetForm();
        loadData();
      }
    } catch (err: any) {
      console.error('Gift card creation error:', err);
      console.error('Error response:', err.response?.data);
      setError(err.response?.data?.message || err.response?.data?.error || 'Oluşturma sırasında hata oluştu');
    }
  };

  const handleUpdateDefinition = async () => {
    if (!editingDefinition) return;
    try {
      setError('');
      const response = await axios.put(
        `${API_BASE_URL}/api/gift-cards/definitions/${editingDefinition.id}`,
        formData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Gift card güncellendi!');
        setOpenDefinitionDialog(false);
        setEditingDefinition(null);
        resetForm();
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Güncelleme sırasında hata oluştu');
    }
  };

  const handleDeleteDefinition = async (id: string) => {
    if (!window.confirm('Bu gift card tanımını silmek istediğinizden emin misiniz?')) return;
    try {
      setError('');
      const response = await axios.delete(
        `${API_BASE_URL}/api/gift-cards/definitions/${id}`,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess('Gift card silindi!');
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Silme sırasında hata oluştu');
    }
  };

  const handleGenerateCodes = async () => {
    try {
      setError('');
      const response = await axios.post(
        `${API_BASE_URL}/api/gift-cards/codes/generate`,
        codeGenData,
        getAuthHeaders()
      );

      if (response.data.success) {
        setSuccess(`${codeGenData.count} adet kod oluşturuldu!`);
        setOpenCodeGenDialog(false);
        setCodeGenData({ definitionId: '', count: 10 });
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kod oluşturma sırasında hata oluştu');
    }
  };

  const resetForm = () => {
    setFormData({
      cafeId: '',
      cafeName: '',
      cafeSlug: '',
      beanCost: 400,
      value: 100,
    });
  };

  const openEditDialog = (definition: GiftCardDefinition) => {
    setEditingDefinition(definition);
    setFormData({
      cafeId: definition.cafeId,
      cafeName: definition.cafeName,
      cafeSlug: definition.cafeSlug,
      beanCost: definition.beanCost,
      value: definition.value,
    });
    setOpenDefinitionDialog(true);
  };

  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            🎁 Gift Card Yönetimi
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* Stats Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Toplam Tanım
              </Typography>
              <Typography variant="h4">{stats.totalDefinitions}</Typography>
              <Typography variant="caption" color="success.main">
                {stats.activeDefinitions} aktif
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Toplam Kod
              </Typography>
              <Typography variant="h4">{stats.totalCodes}</Typography>
              <Typography variant="caption" color="primary.main">
                {stats.availableCodes} müsait
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Satılan Kodlar
              </Typography>
              <Typography variant="h4">{stats.purchasedCodes}</Typography>
              <Typography variant="caption" color="warning.main">
                {stats.usedCodes} kullanıldı
              </Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Toplam Gelir
              </Typography>
              <Typography variant="h4">☕ {stats.totalRevenue}</Typography>
              <Typography variant="caption">Bean</Typography>
            </CardContent>
          </Card>
        </Box>

        <Paper>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
            <Tab label="Gift Card Tanımları" />
            <Tab label="Kodlar" />
          </Tabs>

          {/* Tab 1: Definitions */}
          <TabPanel value={tabValue} index={0}>
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setOpenDefinitionDialog(true)}
              >
                Yeni Gift Card Ekle
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadData}
              >
                Yenile
              </Button>
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Kafe</TableCell>
                    <TableCell>Slug</TableCell>
                    <TableCell>Değer (TL)</TableCell>
                    <TableCell>Maliyet (Bean)</TableCell>
                    <TableCell>Müsait Kod</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {definitions.map((def) => (
                    <TableRow key={def.id}>
                      <TableCell>{def.cafeName}</TableCell>
                      <TableCell>{def.cafeSlug}</TableCell>
                      <TableCell>{def.value} TL</TableCell>
                      <TableCell>☕ {def.beanCost}</TableCell>
                      <TableCell>{def.availableCodes}</TableCell>
                      <TableCell>
                        <Chip
                          label={def.isActive ? 'Aktif' : 'Pasif'}
                          color={def.isActive ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setCodeGenData({ definitionId: def.id, count: 10 });
                            setOpenCodeGenDialog(true);
                          }}
                        >
                          <QrCodeIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => openEditDialog(def)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteDefinition(def.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* Tab 2: Codes */}
          <TabPanel value={tabValue} index={1}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Kod</TableCell>
                    <TableCell>Random Part</TableCell>
                    <TableCell>Durum</TableCell>
                    <TableCell>Satın Alan</TableCell>
                    <TableCell>Satın Alma Tarihi</TableCell>
                    <TableCell>Kullanım Tarihi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {codes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{code.code}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{code.randomPart}</TableCell>
                      <TableCell>
                        <Chip
                          label={code.status}
                          color={
                            code.status === 'available' ? 'success' :
                            code.status === 'purchased' ? 'warning' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{code.purchasedBy || '-'}</TableCell>
                      <TableCell>
                        {code.purchasedAt ? new Date(code.purchasedAt).toLocaleDateString('tr-TR') : '-'}
                      </TableCell>
                      <TableCell>
                        {code.usedAt ? new Date(code.usedAt).toLocaleDateString('tr-TR') : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>

        {/* Definition Dialog */}
        <Dialog open={openDefinitionDialog} onClose={() => {
          setOpenDefinitionDialog(false);
          setEditingDefinition(null);
          resetForm();
        }} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingDefinition ? 'Gift Card Düzenle' : 'Yeni Gift Card Oluştur'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              {!editingDefinition && (
                <FormControl fullWidth margin="normal">
                  <InputLabel>Partner Kafe Seç</InputLabel>
                  <Select
                    value={formData.cafeId}
                    label="Partner Kafe Seç"
                    onChange={(e) => handleCafeSelect(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Kafe seçin...</em>
                    </MenuItem>
                    {partnerCafes.map((cafe) => (
                      <MenuItem key={cafe.id} value={cafe.id}>
                        {cafe.name} ({cafe.slug})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <TextField
                fullWidth
                margin="normal"
                label="Kafe Adı"
                value={formData.cafeName}
                disabled
                helperText="Partner kafe seçildiğinde otomatik dolar"
              />
              <TextField
                fullWidth
                margin="normal"
                label="Kafe Slug"
                value={formData.cafeSlug}
                disabled
                helperText="Partner kafe seçildiğinde otomatik dolar"
              />
              <TextField
                fullWidth
                margin="normal"
                type="number"
                label="Değer (TL)"
                value={formData.value}
                onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) })}
                helperText="Müşteriye verilecek gift card değeri"
              />
              <TextField
                fullWidth
                margin="normal"
                type="number"
                label="Maliyet (Bean)"
                value={formData.beanCost}
                onChange={(e) => setFormData({ ...formData, beanCost: parseInt(e.target.value) })}
                helperText="Kullanıcının ödeyeceği bean miktarı"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => {
              setOpenDefinitionDialog(false);
              setEditingDefinition(null);
              resetForm();
            }}>
              İptal
            </Button>
            <Button
              variant="contained"
              onClick={editingDefinition ? handleUpdateDefinition : handleCreateDefinition}
            >
              {editingDefinition ? 'Güncelle' : 'Oluştur'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Code Generation Dialog */}
        <Dialog open={openCodeGenDialog} onClose={() => setOpenCodeGenDialog(false)}>
          <DialogTitle>Kod Üret</DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              Format: cafeSlug-xxxx-xxxx (örn: crpsso-a7km-m9p4)
            </Alert>
            <TextField
              fullWidth
              margin="normal"
              type="number"
              label="Kod Sayısı"
              value={codeGenData.count}
              onChange={(e) => setCodeGenData({ ...codeGenData, count: parseInt(e.target.value) })}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCodeGenDialog(false)}>İptal</Button>
            <Button variant="contained" onClick={handleGenerateCodes}>
              Üret
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default GiftCardsManagementPage;
